'use client';

import React, { useMemo } from 'react';
import * as d3 from 'd3';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useGetProviderVolumeQuery } from 'src/api/redux';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { claimsOrEncounters } from 'src/components/Profiles/shared/widgets/utils';

const BAR_HEIGHT = 36;
const BAR_RX = BAR_HEIGHT / 2;
const BAR_MIN_PCT = 8;

const CodeSetVolumeGapV2: React.FC = () => {
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const { entityDetails } = useProfile();
  const entityId = entityDetails?.id;
  const { showEncountersTotals } = useFlags();

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    minYear != null || maxYear != null
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  // Support both gap widgets (code_set / comparison_code_set)
  // and ratio widgets (denominator = whole, numerator = part)
  const sourceCodeSetId =
    widget?.code_set?.id ?? widget?.denominator_code_set?.id;
  const sourceEncounterId =
    widget?.encounter?.id ?? widget?.denominator_encounter?.id;
  const comparisonCodeSetId =
    widget?.comparison_code_set?.id ?? widget?.numerator_code_set?.id;
  const comparisonEncounterId =
    widget?.comparison_encounter?.id ?? widget?.numerator_encounter?.id;

  const hasSourceId = !!(sourceCodeSetId || sourceEncounterId);
  const hasComparisonId = !!(comparisonCodeSetId || comparisonEncounterId);

  const { data: sourceData, isLoading: sourceLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        filter_set: sourceCodeSetId ?? undefined,
        cohort_id: sourceEncounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !hasSourceId }
    );

  const { data: comparisonData, isLoading: comparisonLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        filter_set: comparisonCodeSetId ?? undefined,
        cohort_id: comparisonEncounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !hasComparisonId }
    );

  const isLoading = sourceLoading || comparisonLoading;

  const sourceLabel =
    widget?.code_set?.label ??
    widget?.denominator_code_set?.label ??
    widget?.encounter?.label ??
    widget?.denominator_encounter?.label ??
    'Source';
  const comparisonLabel =
    widget?.comparison_code_set?.label ??
    widget?.numerator_code_set?.label ??
    widget?.comparison_encounter?.label ??
    widget?.numerator_encounter?.label ??
    'Comparison';

  const displayFormat = widget?.display_format ?? 'percentage';

  // Source = denominator side, Comparison = numerator side.
  // New widgets send per-slot volume types; legacy widgets send a single volume_type.
  const sourceVolumeType =
    widget?.denominator_volume_type ?? widget?.volume_type;
  const comparisonVolumeType =
    widget?.numerator_volume_type ?? widget?.volume_type;

  const sourceVolumeLabel =
    sourceVolumeType === 'patients' ? 'Patients' : 'Claims';
  const comparisonVolumeLabel =
    comparisonVolumeType === 'patients' ? 'Patients' : 'Claims';

  // For a cohort (encounter) slot, the claims role reads encounters when flagged.
  const sourceClaims = claimsOrEncounters(
    sourceData,
    !!sourceEncounterId,
    showEncountersTotals
  );
  const comparisonClaims = claimsOrEncounters(
    comparisonData,
    !!comparisonEncounterId,
    showEncountersTotals
  );
  const sourceVolume =
    sourceVolumeType === 'patients'
      ? Number(sourceData?.patients ?? 0)
      : sourceVolumeType === 'claims'
        ? Number(sourceClaims ?? 0)
        : Number(sourceClaims ?? sourceData?.patients ?? 0);
  const comparisonVolume =
    comparisonVolumeType === 'patients'
      ? Number(comparisonData?.patients ?? 0)
      : comparisonVolumeType === 'claims'
        ? Number(comparisonClaims ?? 0)
        : Number(comparisonClaims ?? comparisonData?.patients ?? 0);
  const percentage =
    sourceVolume > 0 ? Math.round((comparisonVolume / sourceVolume) * 100) : 0;

  const ratio = useMemo(() => {
    if (displayFormat !== 'ratio') return null;
    if (sourceVolume <= 0) return 0;
    return Math.round((comparisonVolume / sourceVolume) * 100) / 100;
  }, [displayFormat, comparisonVolume, sourceVolume]);

  const barWidth = useMemo(() => {
    if (percentage <= 0) return 0;
    const scale = d3
      .scaleLinear()
      .domain([0, 100])
      .range([BAR_MIN_PCT, 100])
      .clamp(true);
    return scale(percentage);
  }, [percentage]);

  const hasGapData = hasSourceId && hasComparisonId;

  if (isLoading) {
    return <WidgetLoading data-testid="code-set-volume-gap-v2-loading" aria-label="Loading gap data" />;
  }

  if (!hasGapData) {
    return <WidgetEmpty data-testid="code-set-volume-gap-v2-empty" />;
  }

  const volumeRows = (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-sm text-neutral-700">{comparisonLabel}</span>
          <span className="text-xs font-semibold text-neutral-400">{comparisonVolumeLabel}</span>
        </div>
        <span
          className="text-sm font-semibold text-neutral-800 tabular-nums"
          data-testid="code-set-volume-gap-v2-comparison"
        >
          {handleNumericValue(comparisonVolume)}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-sm text-neutral-700">{sourceLabel}</span>
          <span className="text-xs font-semibold text-neutral-400">{sourceVolumeLabel}</span>
        </div>
        <span
          className="text-sm font-semibold text-neutral-800 tabular-nums"
          data-testid="code-set-volume-gap-v2-source"
        >
          {handleNumericValue(sourceVolume)}
        </span>
      </div>
    </div>
  );

  if (displayFormat === 'ratio') {
    return (
      <div className="flex flex-col gap-3" data-testid="code-set-volume-gap-v2">
        <div
          className="flex items-baseline gap-1"
          data-testid="gap-ratio-hero"
          role="img"
          aria-label={`${ratio!.toFixed(2)} to 1 ratio`}
        >
          <span className="text-4xl font-bold text-sky-600 tabular-nums">
            {ratio!.toFixed(2)}
          </span>
          <span className="text-4xl font-bold text-neutral-400 tabular-nums">
            : 1
          </span>
        </div>
        {volumeRows}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="code-set-volume-gap-v2">
      <svg
        className="w-full"
        height={BAR_HEIGHT}
        role="img"
        aria-label={`${percentage}% gap`}
        data-testid="gap-bar"
      >
        <rect
          width="100%"
          height={BAR_HEIGHT}
          rx={BAR_RX}
          className="fill-neutral-200"
          data-testid="gap-bar-track"
        />
        {barWidth > 0 && (
          <rect
            width={`${barWidth}%`}
            height={BAR_HEIGHT}
            rx={BAR_RX}
            className="fill-red-500"
            data-testid="gap-bar-fill"
          />
        )}
        <text
          x={barWidth > 0 ? `${barWidth / 2}%` : '50%'}
          y={BAR_HEIGHT / 2}
          textAnchor="middle"
          dominantBaseline="central"
          className={`text-sm font-semibold ${barWidth > 0 ? 'fill-white' : 'fill-neutral-500'}`}
          data-testid="gap-bar-label"
        >
          {percentage}%
        </text>
      </svg>
      {volumeRows}
    </div>
  );
};

export default CodeSetVolumeGapV2;
